import { useState, useEffect } from 'react';
import { propertyService, leadService, Property } from '../../lib/supabase';
import PropertyGalleryModal from '../../components/PropertyGalleryModal';
import PropertyInquiryModal from '../../components/PropertyInquiryModal';

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCalculatorModal, setShowCalculatorModal] = useState(false);
  const [showLeadMagnetModal, setShowLeadMagnetModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState<string | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showFAQ, setShowFAQ] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Property[]>([]);
  
  // Advanced search filters
  const [searchFilters, setSearchFilters] = useState({
    location: '',
    minSize: '',
    maxSize: '',
    minPrice: '',
    maxPrice: ''
  });
  
  // Property modal states
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  
  const [calculatorData, setCalculatorData] = useState({
    price: '',
    commission: '3',
    notary: '1500',
    other: ''
  });

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    filterProperties();
  }, [properties, activeFilter]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const data = await propertyService.getAll();
      setProperties(data);
    } catch (error) {
      console.error('Failed to load properties:', error);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const filterProperties = () => {
    let filtered = [...properties];
    
    if (activeFilter === 'apartments') {
      filtered = filtered.filter(p => p.type === 'Apartment' && p.listing_type === 'Prodaja');
    } else if (activeFilter === 'houses') {
      filtered = filtered.filter(p => p.type === 'House' && p.listing_type === 'Prodaja');
    } else if (activeFilter === 'rentals') {
      filtered = filtered.filter(p => p.listing_type === 'Najam');
    } else if (activeFilter === 'land') {
      filtered = filtered.filter(p => p.type === 'Land');
    } else if (activeFilter === 'under-market') {
      filtered = filtered.filter(p => p.is_under_market === true);
    }
    
    setFilteredProperties(filtered);
  };

  const parseNaturalLanguageSearch = (query: string) => {
    const lowerQuery = query.toLowerCase();
    let type = '';
    let location = '';
    let maxPrice = 0;

    if (lowerQuery.includes('stan') || lowerQuery.includes('apartman')) {
      type = 'Apartment';
    } else if (lowerQuery.includes('kuća') || lowerQuery.includes('kuca')) {
      type = 'House';
    } else if (lowerQuery.includes('poslovni') || lowerQuery.includes('ured') || lowerQuery.includes('lokal')) {
      type = 'Commercial';
    }

    const locations = [
      'trnje', 'špansko', 'spanjsko', 'trešnjevka', 'tresnjevka', 'maksimir', 
      'dubrava', 'novi zagreb', 'centar', 'gornji grad', 'donji grad', 'upper town',
      'črnomerec', 'crnomerec', 'sesvete', 'peščenica', 'pescenica', 'stenjevec',
      'podsused', 'vrapče', 'vrapce', 'medvedgrad', 'ilica'
    ];
    
    for (const loc of locations) {
      if (lowerQuery.includes(loc)) {
        location = loc;
        break;
      }
    }

    const pricePatterns = [
      /do\s*(\d+(?:\.\d+)?)\s*k(?:\s*eur)?/,
      /do\s*(\d+(?:\s*\d+)*)\s*(?:000)?\s*eur/,
      /do\s*(\d+(?:\s*\d+)*)/,
      /max\s*(\d+(?:\.\d+)?)\s*k/,
      /maksimalno\s*(\d+(?:\.\d+)?)\s*k/
    ];

    for (const pattern of pricePatterns) {
      const priceMatch = lowerQuery.match(pattern);
      if (priceMatch) {
        let price = parseFloat(priceMatch[1].replace(/\s/g, ''));
        if ((lowerQuery.includes('k') || pattern.source.includes('k')) && price < 1000) {
          price *= 1000;
        }
        maxPrice = price;
        break;
      }
    }

    return { type, location, maxPrice };
  };

  const handleNaturalLanguageSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const { type, location, maxPrice } = parseNaturalLanguageSearch(searchQuery);
    
    const allProperties = properties.length > 0 ? properties : mockProperties;
    let filtered = [...allProperties];

    if (type) {
      filtered = filtered.filter(p => p.type === type);
    }

    if (location) {
      filtered = filtered.filter(p => 
        p.location.toLowerCase().includes(location)
      );
    }

    if (maxPrice > 0) {
      filtered = filtered.filter(p => p.price <= maxPrice);
    }

    setSearchResults(filtered);
  };

  const handleFilterChange = (filterType: string) => {
    setActiveFilter(filterType);
    setSearchQuery('');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const leadData = {
        name: formData.get('name') as string,
        email: formData.get('email') as string || '',
        phone: formData.get('phone') as string || '',
        message: `Vrsta nekretnine: ${formData.get('property_type') || 'N/A'}, Budžet: ${formData.get('budget') || 'N/A'} EUR`,
        source: 'kontakt forma'
      };

      await leadService.create(leadData);
      
      setFormStatus('success');
      form.reset();
      setTimeout(() => {
        setFormStatus('idle');
        setShowContactModal(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting lead:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const calculateTotal = () => {
    const price = parseFloat(calculatorData.price) || 0;
    const commission = (price * parseFloat(calculatorData.commission)) / 100;
    const notary = parseFloat(calculatorData.notary) || 0;
    const other = parseFloat(calculatorData.other) || 0;
    const totalCosts = commission + notary + other;
    const totalWithProperty = price + totalCosts;
    
    return { totalCosts, totalWithProperty };
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const mockProperties = [
    {
      id: 'ZG001',
      title: 'Luxury Penthouse with Cathedral View',
      location: 'Upper Town, Zagreb',
      price: 320000,
      pricePerSqm: 3368,
      bedrooms: 3,
      bathrooms: 2,
      area: 95,
      type: 'Apartment',
      description: 'Stunning three-bedroom penthouse in Zagreb\'s historic Upper Town. Features panoramic views of Zagreb Cathedral, premium finishes, and rooftop terrace.',
      features: ['Rooftop Terrace', 'Premium Finishes', 'Elevator'],
      image: 'https://public.readdy.ai/ai/img_res/e45a7bbb9681ad20f93770d09e9bb00f.jpg',
      is_under_market: false
    },
    {
      id: 'ZG002',
      title: 'Modern Family Apartment in Trnje',
      location: 'Trnje, Zagreb',
      price: 165000,
      pricePerSqm: 2292,
      bedrooms: 2,
      bathrooms: 1,
      area: 72,
      type: 'Apartment',
      description: 'Bright two-bedroom apartment in popular Trnje district. Recently renovated with modern amenities.',
      features: ['Parking Space', 'Balcony', 'Central Heating'],
      image: 'https://readdy.ai/api/search-image?query=Modern%20two%20bedroom%20apartment%20Trnje%20Zagreb%2C%20bright%20living%20space%2C%20contemporary%20interior%20design%2C%20family%20friendly%20environment%2C%20natural%20lighting%2C%20Arena%20Zagreb%20nearby%2C%20professional%20real%20estate%20photography&width=800&height=600&seq=3&orientation=landscape',
      is_under_market: false
    },
    {
      id: 'ZG003',
      title: 'Designer Villa near Maksimir Park',
      location: 'Maksimir, Zagreb',
      price: 485000,
      pricePerSqm: 2487,
      bedrooms: 4,
      bathrooms: 3,
      area: 195,
      type: 'House',
      description: 'Architect-designed family villa just minutes from Maksimir Park and Zagreb Zoo.',
      features: ['Landscaped Garden', 'Double Garage', 'Designer Kitchen'],
      image: 'https://readdy.ai/api/search-image?query=Designer%20family%20villa%20Maksimir%20Zagreb%20Croatia%2C%20contemporary%20architecture%2C%20landscaped%20garden%2C%20Maksimir%20park%20nearby%2C%20elegant%20exterior%20design%2C%20open%20plan%20living%2C%20professional%20real%20estate%20photography&width=800&height=600&seq=4&orientation=landscape',
      is_under_market: false
    }
  ];

  const handleAdvancedSearch = () => {
    const allProperties = properties.length > 0 ? properties : mockProperties;
    let filtered = [...allProperties];

    // Filter by location
    if (searchFilters.location.trim()) {
      const locationQuery = searchFilters.location.toLowerCase();
      filtered = filtered.filter(p => 
        p.location.toLowerCase().includes(locationQuery)
      );
    }

    // Filter by size
    if (searchFilters.minSize) {
      const minSize = parseFloat(searchFilters.minSize);
      filtered = filtered.filter(p => p.area >= minSize);
    }

    if (searchFilters.maxSize) {
      const maxSize = parseFloat(searchFilters.maxSize);
      filtered = filtered.filter(p => p.area <= maxSize);
    }

    // Filter by price
    if (searchFilters.minPrice) {
      const minPrice = parseFloat(searchFilters.minPrice);
      filtered = filtered.filter(p => {
        const price = p.listing_type === 'Najam' ? p.rental_price : p.price;
        return price && price >= minPrice;
      });
    }

    if (searchFilters.maxPrice) {
      const maxPrice = parseFloat(searchFilters.maxPrice);
      filtered = filtered.filter(p => {
        const price = p.listing_type === 'Najam' ? p.rental_price : p.price;
        return price && price <= maxPrice;
      });
    }

    setSearchResults(filtered);
  };

  const resetSearchFilters = () => {
    setSearchFilters({
      location: '',
      minSize: '',
      maxSize: '',
      minPrice: '',
      maxPrice: ''
    });
    setSearchResults([]);
  };

  // Limit to 6 properties (2 rows of 3)
  const displayProperties = properties.length > 0 ? filteredProperties.slice(0, 6) : mockProperties.filter(property => {
    if (activeFilter === 'apartments') return property.type === 'Apartment';
    if (activeFilter === 'houses') return property.type === 'House';
    if (activeFilter === 'under-market') return property.is_under_market === true;
    return true;
  }).slice(0, 6);

  const serviceModals = {
    'credit': {
      title: 'Kreditno posredništvo',
      description: 'Uspoređujemo ponude svih banaka i osiguravamo povoljnije kamatne stope za vašu nekretnu.',
      features: [
        'Usporedba ponuda svih banaka u realnom vremenu',
        'Pregovaranje povoljnijih kamatnih stopa',
        'Potpuna priprema dokumentacije',
        'Praćenje procesa do odobrenja kredita'
      ],
      cta: 'Zatražite kreditno posredništvo'
    },
    'energy': {
      title: 'Energetsko certificiranje',
      description: 'Brzo i pouzdano energetsko certificiranje kroz našu mrežu ovlaštenih certifikatora.',
      features: [
        'Brza organizacija izrade energetskog certifikata',
        'Suradnja s ovlaštenim certifikatorima',
        'Usklađenost s važećim propisima',
        'Dostava certifikata u kratkom roku'
      ],
      cta: 'Zatražite energetski certifikat'
    },
    'renovation': {
      title: 'Adaptacije',
      description: 'Kompleksne adaptacije s provjerenim izvođačima kroz našu mrežu licenciranih partnera.',
      features: [
        'Projektiranje i dizajn interijera',
        'Koordinacija svih zanatlija',
        'Nabava materijala po veleprodajnim cijenama',
        'Nadzor kvalitete i rokova izvođenja'
      ],
      cta: 'Zatražite ponudu za adaptaciju'
    },
    'legal': {
      title: 'Pravna podrška',
      description: 'Potpuna pravna sigurnost transakcije kroz detaljnu provjeru svih aspekata nekretnine.',
      features: [
        'Provjera vlasništva i teretovnice nekretnine',
        'Kontrola dugovanja prema komunalnim službama',
        'Provjera građevinskih i uporabnih dozvola',
        'Analiza pravnih ograničenja i servituta'
      ],
      cta: 'Zatražite pravnu provjeru'
    },
    'utilities': {
      title: 'Prijenos režija',
      description: 'Brza promjena korisnika za sve komunalne usluge i režije.',
      features: [
        'Prijenos struje (HEP)',
        'Prijenos plina (Gradska plinara Zagreb)',
        'Prijenos vode i odvodnje',
        'Organizacija internet i TV usluga'
      ],
      cta: 'Zatražite prijenos režija'
    },
    'consultinging': {
      title: 'Savjetovanje',
      description: 'Struna procjena tržišne vrijednosti i analiza kvarta.',
      features: [
        'Procjena tržišne vrijednosti nekretnine',
        'Analiza kvarta i infrastrukture',
        'Prognoza rasta vrijednosti',
        'Usporedba s konkurentskim ponudama'
      ],
      cta: 'Zatražite stručno savjetovanje'
    }
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const leadData = {
        name: formData.get('name') as string,
        email: formData.get('email') as string || '',
        phone: formData.get('phone') as string || '',
        message: `Vrsta nekretnine: ${formData.get('property_type') || 'N/A'}, Budžet: ${formData.get('budget') || 'N/A'} EUR`,
        source: 'kontakt modal'
      };

      await leadService.create(leadData);
      
      setFormStatus('success');
      form.reset();
      setTimeout(() => {
        setFormStatus('idle');
        setShowContactModal(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting contact lead:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const handleServiceSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const serviceType = formData.get('service_type') as string;
      const leadData = {
        name: formData.get('name') as string,
        email: '',
        phone: formData.get('phone') as string || '',
        message: formData.get('message') as string || '',
        source: `usluga: ${serviceType}`
      };

      await leadService.create(leadData);
      
      setFormStatus('success');
      form.reset();
      setTimeout(() => {
        setFormStatus('idle');
        setShowServiceModal(null);
      }, 3000);
    } catch (error) {
      console.error('Error submitting service lead:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const handlePropertyClick = (property: Property) => {
    setSelectedProperty(property);
    setShowGalleryModal(true);
    setShowInquiryModal(false);
  };

  const handleInquiryClick = () => {
    setShowGalleryModal(false);
    setTimeout(() => {
      setShowInquiryModal(true);
    }, 300);
  };

  const handleCloseGallery = () => {
    setShowGalleryModal(false);
    setSelectedProperty(null);
  };

  const handleCloseInquiry = () => {
    setShowInquiryModal(false);
    setSelectedProperty(null);
  };

  const handleInquirySuccess = () => {
    setShowInquiryModal(false);
    setSelectedProperty(null);
  };

  const getPropertyTypeLabel = (type: string) => {
    switch (type) {
      case 'Apartment':
        return 'Stan';
      case 'House':
        return 'Kuća';
      case 'Land':
        return 'Zemljište';
      case 'Commercial':
        return 'Poslovni prostor';
      default:
        return type;
    }
  };

  return (
    <div className="min-h-screen bg-white text-base">
      {/* Property Modals */}
      {selectedProperty && (
        <>
          <PropertyGalleryModal
            property={selectedProperty}
            isOpen={showGalleryModal}
            onClose={handleCloseGallery}
            onInquiryClick={handleInquiryClick}
          />
          
          <PropertyInquiryModal
            property={selectedProperty}
            isOpen={showInquiryModal}
            onClose={handleCloseInquiry}
            onSuccess={handleInquirySuccess}
          />
        </>
      )}

      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-shrink-0 cursor-pointer mr-8" onClick={scrollToTop}>
              <img 
                src="https://static.readdy.ai/image/543888419322a681fa4fe0b45060a131/83927b54a7909cd8f2d054a2d7c5ece9.png" 
                alt="BuyInZagreb Logo" 
                className="h-10 w-auto"
              />
            </div>
            
            <nav className="hidden lg:flex items-center space-x-6 flex-1">
              <button onClick={scrollToTop} className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                Početna
              </button>
              <a href="/nekretnine" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                Nekretnine
              </a>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                Usluge
              </button>
              <a href="/besplatna-procjena-nekretnine-zagreb" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                Besplatna procjena
              </a>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                O nama
              </button>
              <button onClick={() => scrollToSection('blog-tools')} className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                Blog
              </button>
              <button onClick={() => setShowContactModal(true)} className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                Kontakt
              </button>
            </nav>

            <div className="flex items-center space-x-3 flex-shrink-0">
              <button 
                onClick={() => setShowSearchModal(true)}
                className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 cursor-pointer"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
              <button onClick={() => setShowContactModal(true)} className="hidden lg:flex bg-red-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-red-700 hover:shadow-lg transition-all duration-200 cursor-pointer whitespace-nowrap">
                Kontaktirajte nas
              </button>
              <button 
                className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 cursor-pointer"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
            <nav className="px-4 py-4 space-y-3">
              <button onClick={() => { scrollToTop(); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors">
                Početna
              </button>
              <a href="/nekretnine" className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors">
                Nekretnine
              </a>
              <button onClick={() => { scrollToSection('services'); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors">
                Usluge
              </button>
              <a href="/besplatna-procjena-nekretnine-zagreb" className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors">
                Besplatna procjena
              </a>
              <button onClick={() => { scrollToSection('about'); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors">
                O nama
              </button>
              <button onClick={() => { scrollToSection('blog-tools'); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors">
                Blog
              </button>
              <button onClick={() => { setShowContactModal(true); setMobileMenuOpen(false); }} className="block w-full bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors text-center">
                Kontaktirajte nas
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-2xl rounded-3xl p-8 max-w-2xl w-full shadow-2xl border border-white/30 relative max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setShowContactModal(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 rounded-lg transition-all duration-200 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            
            <div className="mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Kontaktirajte nas</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Pošaljite nam upit i kontaktirat ćemo vas u najkraćem mogućem roku.
              </p>
            </div>
            
            <form id="contact-form" data-readdy-form onSubmit={handleContactSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Ime i prezime *</label>
                <input 
                  type="text"
                  name="name"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                  placeholder="Vaše ime i prezime"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Kontakt telefon *</label>
                <input 
                  type="tel"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                  placeholder="+385 xx xxx xxxx"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Vrsta nekretnine koju tražite *</label>
                <select 
                  name="property_type"
                  required
                  className="w-full px-4 py-3 pr-8 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                >
                  <option value="">Odaberite vrstu nekretnine</option>
                  <option value="stan">Stan</option>
                  <option value="kuca">Kuća</option>
                  <option value="poslovni-prostor">Poslovni prostor</option>
                  <option value="zemljiste">Zemljište</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Budžet (EUR)</label>
                <input 
                  type="number"
                  name="budget"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                  placeholder="Vaš budžet u eurima"
                />
              </div>
              
              <div className="flex items-start space-x-3">
                <input 
                  type="checkbox"
                  id="gdpr-consent"
                  name="gdpr_consent"
                  required
                  className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <label htmlFor="gdpr-consent" className="text-sm text-gray-600 leading-relaxed">
                  Suglasan/na sam da se moji podaci koriste isključivo za kontakt i ponudu nekretnina.
                </label>
              </div>
              
              <button 
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-red-600 text-white py-4 rounded-xl text-lg font-medium hover:bg-red-700 disabled:bg-gray-400 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'submitting' ? 'Šalje se...' : formStatus === 'success' ? 'Vaš upit je zaprimljen!' : formStatus === 'error' ? 'Greška - pokušajte ponovo' : 'Pošalji upit'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Service Modal */}
      {showServiceModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-2xl rounded-3xl p-8 max-w-2xl w-full shadow-2xl border border-white/30 relative max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setShowServiceModal(null)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 rounded-lg transition-all duration-200 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            
            <div className="mb-8">
              <h3 className="text-3xl font-bold text-gray-900">
                {serviceModals[showServiceModal as keyof typeof serviceModals]?.title}
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {serviceModals[showServiceModal as keyof typeof serviceModals]?.description}
              </p>
              
              <div className="space-y-3 mb-8">
                {serviceModals[showServiceModal as keyof typeof serviceModals]?.features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-3 text-lg"></i>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-red-50 rounded-2xl p-6 mb-8">
                <h4 className="text-xl font-bold text-red-600 mb-4">
                  {serviceModals[showServiceModal as keyof typeof serviceModals]?.cta}
                </h4>
                
                <form onSubmit={handleServiceSubmit} className="space-y-4">
                  <input type="hidden" name="service_type" value={showServiceModal} />
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Ime i prezime *</label>
                    <input 
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                      placeholder="Vaše ime i prezime"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Kontakt telefon *</label>
                    <input 
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                      placeholder="+385 xx xxx xxxx"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Napomena</label>
                    <textarea 
                      name="message"
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all resize-none"
                      placeholder="Dodatne informacije ili pitanja..."
                    ></textarea>
                  </div>
                  
                  <button 
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full bg-red-600 text-white py-3 rounded-xl font-medium hover:bg-red-700 disabled:bg-gray-400 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? 'Šalje se...' : formStatus === 'success' ? 'Vaš upit je zaprimljen!' : 'Pošalji upit'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Search Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white/95 backdrop-blur-2xl rounded-2xl p-8 max-w-4xl w-full shadow-2xl border border-white/30 relative mt-8 mb-8">
            <button 
              onClick={() => {
                setShowSearchModal(false);
                setSearchResults([]);
                setSearchQuery('');
              }}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 rounded-lg transition-all duration-200 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            
            <div className="mb-6">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">Pronađite nekretnine na napredan način</h3>
              <p className="text-gray-600">Pretražite naš portfelj nekretnina po kvartu, cijeni ili kvadraturi</p>
            </div>
            
            <div className="space-y-6">
              <div className="relative">
                <input 
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleNaturalLanguageSearch()}
                  className="w-full px-6 py-4 pr-12 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all text-lg"
                  placeholder="npr. stan trnje do 200k eura"
                />
                <button 
                  onClick={handleNaturalLanguageSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-search-line text-lg"></i>
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Pronađeno: {searchResults.length} {searchResults.length === 1 ? 'nekretnina' : 'nekretnine'}
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                    {searchResults.map((property) => {
                      const isRental = property.listing_type === 'Najam';
                      const displayPrice = isRental ? property.rental_price : property.price;
                      const pricePerSqm = displayPrice && property.area 
                        ? Math.round(displayPrice / property.area) 
                        : property.price_per_sqm || 0;
                      
                      return (
                        <div 
                          key={property.id} 
                          className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 cursor-pointer"
                          onClick={() => {
                            setShowSearchModal(false);
                            handlePropertyClick(property);
                          }}
                        >
                          <div className="relative">
                            <img 
                              src={(property.images && property.images.length > 0) ? property.images[0] : (property.image || `https://readdy.ai/api/search-image?query=Modern%20$%7Bproperty.type.toLowerCase%28%29%7D%20$%7Bproperty.location%7D%20Zagreb%20Croatia%2C%20professional%20real%20estate%20photography&width=400&height=250&seq=${property.id}&orientation=landscape`)}
                              alt={property.title}
                              className="w-full h-40 object-cover object-top"
                            />
                            <div className="absolute top-2 right-2 bg-red-600/90 text-white px-2 py-1 rounded text-xs font-medium">
                              {getPropertyTypeLabel(property.type)}
                            </div>
                            {isRental && (
                              <div className="absolute top-2 left-2 bg-blue-600/90 text-white px-2 py-1 rounded text-xs font-medium">
                                Najam
                              </div>
                            )}
                            {property.is_under_market && (
                              <div className="absolute top-2 left-2 bg-green-600/90 text-white px-2 py-1 rounded text-xs font-medium">
                                Ispod cijene
                              </div>
                            )}
                          </div>
                          
                          <div className="p-4">
                            <h5 className="font-bold text-gray-900 mb-2 text-sm line-clamp-1">{property.title}</h5>
                            <div className="flex items-center text-gray-600 mb-2 text-xs">
                              <i className="ri-map-pin-line mr-1 text-red-500"></i>
                              <span>{property.location}</span>
                            </div>
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-lg font-bold text-red-600">
                                {displayPrice?.toLocaleString()} €{isRental ? '/mj' : ''}
                              </div>
                              <div className="text-xs text-gray-500">
                                {property.area} m²
                              </div>
                            </div>
                            {!isRental && pricePerSqm > 0 && (
                              <div className="text-xs text-gray-600 font-medium bg-red-50 px-2 py-1 rounded inline-block">
                                {pricePerSqm.toLocaleString()} €/m²
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="pt-16">
        {/* Hero Section */}
        <section className="relative min-h-[85vh] lg:min-h-screen flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(https://static.readdy.ai/image/2f9a28576106260affc17d45e08b3b04/08d905b6cf360fe51df535e73d173c6d.jpeg)'
            }}
          ></div>
          
          <div className="absolute inset-0 bg-black/70"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 text-center z-10">
            <div className="max-w-5xl mx-auto">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 lg:mb-8 leading-tight tracking-tight">
                Jedina agencija u Zagrebu koja potpuno eliminira rizike pri kupnji nekretnine.
              </h1>
              
              <div className="text-lg sm:text-xl lg:text-2xl text-white mb-10 lg:mb-16 leading-relaxed max-w-4xl mx-auto font-light">
                <p className="text-white/95">Navigirajte volatilno tržište uz transparentnog posrednika i stručan tim.</p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 justify-center max-w-2xl mx-auto">
                <button 
                  onClick={() => setShowSearchModal(true)}
                  className="group bg-red-600 text-white px-8 lg:px-12 py-4 lg:py-6 rounded-2xl text-lg lg:text-xl font-medium hover:bg-red-700 hover:shadow-2xl hover:shadow-red-500/30 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105 w-full sm:w-auto"
                >
                  <i className="ri-search-line mr-3 group-hover:scale-110 transition-transform"></i>
                  Napredna pretraga
                </button>
                <a 
                  href="/besplatna-procjena-nekretnine-zagreb"
                  className="group bg-white/20 backdrop-blur-xl border border-white/30 text-white px-8 py-4 rounded-xl font-medium hover:bg-white/30 hover:shadow-2xl transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105 w-full sm:w-auto flex items-center justify-center"
                >
                  Besplatna procjena
                  <i className="ri-arrow-right-line ml-3 group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Listings Section */}
        <section id="listings" className="py-12 lg:py-16 bg-gradient-to-br from-gray-50/80 via-white to-slate-50/80 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-6 lg:p-8 shadow-xl shadow-black/5 max-w-4xl mx-auto border border-white/40 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-red-50/50 via-transparent to-blue-50/30 rounded-2xl"></div>
                <div className="relative z-10">
                  <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 mb-4 lg:mb-6 tracking-tight">
                    Provjerene nekretnine Zagreb
                  </h2>
                  <p className="text-base lg:text-lg xl:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
                    Svaka nekretnina prošla je detaljnu provjeru dokumentacije, pravnog statusa i tržišne vrijednosti.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-2 lg:gap-3 mb-8 lg:mb-12">
              {[
                { label: 'Sve nekretnine', key: 'all' },
                { label: 'Prodaja stanova', key: 'apartments' },
                { label: 'Prodaja kuća', key: 'houses' },
                { label: 'Najam stanova', key: 'rentals' },
                { label: 'Zemljišta', key: 'land' },
                { label: 'Ispod cijene', key: 'under-market' }
              ].map((filter, index) => (
                <button 
                  key={index}
                  onClick={() => handleFilterChange(filter.key)}
                  className={`px-4 lg:px-6 py-2 lg:py-3 rounded-xl font-medium transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-xl hover:scale-105 text-sm lg:text-base ${
                    activeFilter === filter.key
                      ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-500/25' 
                      : 'bg-white/20 text-gray-700 hover:text-red-600 border border-white/60 shadow-md shadow-black/5'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-6 shadow-xl shadow-black/10 border border-white/40">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-3 border-red-600/20 border-t-red-600"></div>
                    <span className="text-gray-700 text-base font-medium">Učitavanje...</span>
                  </div>
                </div>
              </div>
            ) : displayProperties.length === 0 ? (
              <div className="text-center py-16">
                <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-8 shadow-xl shadow-black/10 border border-white/40 max-w-md mx-auto">
                  <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Nema rezultata</h3>
                  <p className="text-gray-600 mb-4">Trenutno nemamo nekretnine koje odgovaraju vašoj pretrazi.</p>
                  <button 
                    onClick={() => {
                      setActiveFilter('all');
                      setSearchQuery('');
                    }}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors cursor-pointer"
                  >
                    Prikaži sve nekretnine
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-product-shop="true">
                  {displayProperties.map((property) => {
                    const isRental = property.listing_type === 'Najam';
                    const displayPrice = isRental ? property.rental_price : property.price;
                    const pricePerSqm = displayPrice && property.area 
                      ? Math.round(displayPrice / property.area) 
                      : property.price_per_sqm || 0;
                    
                    return (
                      <div 
                        key={property.id} 
                        className="group bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-500 overflow-hidden cursor-pointer border border-white/50 hover:border-white/70 relative"
                        onClick={() => handlePropertyClick(property)}
                      >
                        <div className="relative overflow-hidden">
                          <img 
                            src={(property.images && property.images.length > 0) ? property.images[0] : (property.image || `https://readdy.ai/api/search-image?query=Modern%20$%7Bproperty.type.toLowerCase%28%29%7D%20$%7Bproperty.location%7D%20Zagreb%20Croatia%2C%20professional%20real%20estate%20photography%2C%20bright%20interior%2C%20contemporary%20design&width=800&height=600&seq=${property.id}&orientation=landscape`)}
                            alt={property.title}
                            className="w-full h-44 lg:h-48 object-cover object-top group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent group-hover:from-black/30 transition-all duration-500"></div>
                          <div className="absolute top-3 right-3 bg-red-600/90 backdrop-blur-sm text-white px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg text-xs lg:text-sm font-medium shadow-lg border border-red-500/30">
                            {getPropertyTypeLabel(property.type)}
                          </div>
                          {property.is_under_market && (
                            <div className="absolute top-3 left-3 bg-green-600/90 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs font-medium flex items-center">
                              <i className="ri-arrow-down-line mr-1"></i>
                              Ispod tržišne cijene
                            </div>
                          )}
                          {isRental && (
                            <div className="absolute top-3 left-3 bg-blue-600/90 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs font-medium flex items-center">
                              <i className="ri-home-heart-line mr-1"></i>
                              Najam
                            </div>
                          )}
                          {property.images && property.images.length > 1 && (
                            <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs font-medium flex items-center">
                              <i className="ri-image-line mr-1"></i>
                              {property.images.length}
                            </div>
                          )}
                        </div>
                        
                        <div className="p-4 lg:p-6">
                          <h3 className="text-base lg:text-lg font-bold text-gray-900 mb-3 lg:mb-4 line-clamp-2 group-hover:text-red-600 transition-colors duration-300">
                            {property.title}
                          </h3>
                          <div className="flex items-center text-gray-600 mb-3 lg:mb-4">
                            <i className="ri-map-pin-line mr-2 text-red-500"></i>
                            <span className="font-medium text-sm">{property.location}</span>
                          </div>
                          
                          <div className="flex items-center justify-between mb-2 lg:mb-3">
                            <div className="text-xl lg:text-2xl font-bold text-red-600">
                              {displayPrice?.toLocaleString()} €{isRental ? '/mj' : ''}
                            </div>
                            <div className="text-xs text-gray-500 font-medium bg-gray-100/80 backdrop-blur-sm px-2 py-1 rounded-lg">
                              ID: {property.id}
                            </div>
                          </div>
                          
                          {!isRental && (
                            <div className="text-sm text-gray-600 mb-3 lg:mb-4 font-medium bg-red-50/80 backdrop-blur-sm px-2 py-1 rounded-lg inline-block">
                              {pricePerSqm?.toLocaleString()} €/m²
                            </div>
                          )}
                          
                          <div className="flex flex-wrap gap-1.5">
                            {property.features?.slice(0, 3).map((feature, index) => (
                              <span key={index} className="px-2 py-1 bg-gray-100/80 backdrop-blur-sm text-gray-700 text-xs rounded-lg font-medium border border-gray-200/60">
                                {feature}
                              </span>
                            ))}
                            {property.features && property.features.length > 3 && (
                              <span className="px-2 py-1 bg-red-100/80 backdrop-blur-sm text-red-600 text-xs rounded-lg font-medium border border-red-200/60">
                                +{property.features.length - 3} više
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="text-center mt-12">
                  <a 
                    href="/nekretnine"
                    className="inline-flex items-center bg-red-600 text-white px-8 py-4 rounded-xl text-lg font-medium hover:bg-red-700 hover:shadow-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    Pogledajte sve nekretnine
                    <i className="ri-arrow-right-line ml-3"></i>
                  </a>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-12 lg:py-16 bg-white relative overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">
                Što radimo za vas
              </h2>
              <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
                Potpuna kontrola procesa od prve provjere do predaje ključeva
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[
                { id: 'credit', icon: 'ri-bank-line', title: 'Kreditno posredništvo', description: 'Pregovaramo s bankama i osiguravamo najbolje uvjete financiranja.' },
                { id: 'energy', icon: 'ri-file-text-line', title: 'Energetsko certificiranje', description: 'Organiziramo izradu certifikata kroz mrežu ovlaštenih partnera.' },
                { id: 'renovation', icon: 'ri-hammer-line', title: 'Adaptacije', description: 'Koordiniramo sve radove s provjerenim izvođačima.' },
                { id: 'legal', icon: 'ri-shield-check-line', title: 'Pravna provjera', description: 'Detaljno analiziramo dokumentaciju i pravni status nekretnine.' },
                { id: 'utilities', icon: 'ri-exchange-line', title: 'Prijenos režija', description: 'Organiziramo promjenu korisnika za sve komunalne usluge.' },
                { id: 'consultinging', icon: 'ri-lightbulb-line', title: 'Stručno savjetovanje', description: 'Procjenjujemo tržišnu vrijednost i analiziramo kvart.' }
              ].map((service, index) => (
                <div 
                  key={index} 
                  className="group bg-white border border-gray-100 rounded-2xl p-6 lg:p-8 shadow-md hover:shadow-xl transition-all duration-500 hover:scale-105 hover:border-red-100 cursor-pointer"
                  onClick={() => setShowServiceModal(service.id)}
                >
                  <div className="w-14 h-14 lg:w-16 lg:h-16 flex items-center justify-center bg-gradient-to-br from-red-600 to-red-500 rounded-xl mb-4 lg:mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg shadow-red-500/25 relative">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center bg-white/20 backdrop-blur-xl rounded-lg">
                      <i className={`${service.icon} text-white text-lg lg:text-xl`}></i>
                    </div>
                  </div>
                  
                  <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-3 lg:mb-4 group-hover:text-red-600 transition-colors duration-300">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed text-sm lg:text-base">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-12 lg:py-16 bg-gray-50 relative overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8 lg:mb-12">
                <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">
                  O nama
                </h2>
              </div>

              <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-md border border-gray-100">
                <div className="space-y-6 text-gray-700 leading-relaxed text-base lg:text-lg">
                  <p>
                    <strong className="text-gray-900">BUY IN ZAGREB d.o.o.</strong> aktivno posluje od 2022. godine i danas je jedna od rijetkih agencija u Zagrebu koja kombinira dubinsku provjeru dokumentacije, analizu tržišnih rizika i potpunu transparentnost u svakom koraku kupoprodaje.
                  </p>
                  
                  <p>
                    Naš portfelj broji više od 100 nekretnina, a iza sebe imamo preko 50 uspješno realiziranih kupnji i prodaja — bez skrivenih detalja, bez nagađanja i bez 'sjedanja na dvije stolice'.
                  </p>
                  
                  <p>
                    Klijentima pružamo vrhunsku dostupnost, jasne informacije prije obilaska, aktivno pregovaranje i potpunu pravnu podršku — od prvog kontakta do preuzimanja ključeva.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-6 mt-10 pt-8 border-t border-gray-200">
                  <div className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-br from-red-600 to-red-500 bg-clip-text text-transparent mb-2">
                      100+
                    </div>
                    <div className="text-gray-700 font-medium text-sm lg:text-base">Nekretnina u portfelju</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-br from-red-600 to-red-500 bg-clip-text text-transparent mb-2">
                      50+
                    </div>
                    <div className="text-gray-700 font-medium text-sm lg:text-base">Realiziranih transakcija</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Section */}
        <section id="blog-tools" className="py-12 lg:py-16 bg-white relative overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">
                Stručne analize
              </h2>
              <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Dubinski uvidi u zagrebačko tržište nekretnina
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {[
                {
                  title: 'Vodič kroz financiranje nekretnine',
                  description: 'Detaljno objašnjenje procesa dobivanja stambenog kredita u Hrvatskoj.',
                  image: 'https://readdy.ai/api/search-image?query=Home%20financing%20mortgage%20consultation%20Zagreb%20Croatia%2C%20financial%20planning%20documents%2C%20calculator%20and%20contracts%2C%20professional%20banking%20environment%2C%20modern%20office%20setting&width=600&height=400&seq=blog-finance&orientation=landscape',
                  link: '/blog/financiranje-nekretnine-zagreb'
                },
                {
                  title: 'Utjecaj inflacije na cijene nekretnina',
                  description: 'Stručna analiza kako ekonomski trendovi utječu na zagrebačko tržište nekretnina.',
                  image: 'https://readdy.ai/api/search-image?query=Real%20estate%20market%20analysis%20Zagreb%20Croatia%2C%20property%20price%20trends%2C%20inflation%20impact%2C%20economic%20charts%20and%20graphs%2C%20professional%20market%20research%20environment&width=600&height=400&seq=blog-inflation&orientation=landscape',
                  link: '/blog/analiza-cijena-nekretnina-zagreb'
                },
                {
                  title: 'Novogradnja ili adaptacija?',
                  description: 'Detaljno poređenje prednosti i nedostataka kupnje novogradnje u odnosu na adaptaciju.',
                  image: 'https://readdy.ai/api/search-image?query=Modern%20new%20construction%20apartment%20building%20Zagreb%20Croatia%2C%20contemporary%20architecture%2C%20glass%20facade%2C%20luxury%20residential%20development%2C%20urban%20skyline%2C%20professional%20real%20estate%20photography%2C%20bright%20daylight&width=600&height=400&seq=blog-new-construction&orientation=landscape',
                  link: '/blog/novogradnja-ili-adaptacija-zagreb'
                }
              ].map((article, index) => (
                <a 
                  key={index}
                  href={article.link}
                  className="group bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 hover:scale-105 hover:border-red-100 cursor-pointer flex flex-col h-full"
                >
                  <div className="aspect-[3/2] overflow-hidden">
                    <img 
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  
                  <div className="p-6 lg:p-8 flex-1 flex flex-col">
                    <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-3 lg:mb-4 line-clamp-2 group-hover:text-red-600 transition-colors duration-300 flex-shrink-0">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed line-clamp-3 text-sm lg:text-base flex-1">
                      {article.description}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-12 lg:py-16 bg-gray-50 relative overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 lg:mb-12">
              <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">
                Kontaktirajte nas
              </h2>
              <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Naš tim licenciranih agenata spreman je pružiti vam stručno savjetovanje
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
              <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center bg-red-600 rounded-xl mb-4 mx-auto">
                  <i className="ri-phone-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 text-center">Telefon</h3>
                <a href="tel:+385953845657" className="block text-gray-700 hover:text-red-600 transition-colors text-center font-medium">
                  095 384 5657
                </a>
              </div>

              <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center bg-red-600 rounded-xl mb-4 mx-auto">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 text-center">Email</h3>
                <a href="mailto:info@buyinzagreb.com" className="block text-gray-700 hover:text-red-600 transition-colors text-center font-medium">
                  info@buyinzagreb.com
                </a>
              </div>

              <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center bg-red-600 rounded-xl mb-4 mx-auto">
                  <i className="ri-map-pin-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 text-center">Adresa</h3>
                <p className="text-gray-700 text-center font-medium">
                  Slavonska avenija 17<br />
                  (Profi Bau Centar)<br />
                  Zagreb
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800/50 via-transparent to-black/50"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-12">
            <div className="space-y-4 lg:space-y-6">
              <h3 className="text-2xl lg:text-3xl font-bold">BuyInZagreb</h3>
              <p className="text-gray-300 leading-relaxed text-sm lg:text-base">
                Vaš partner u bez-rizičnom posredovanju od 2022.
              </p>
              <div className="flex space-x-3 lg:space-x-4">
                {['ri-facebook-fill', 'ri-instagram-line', 'ri-linkedin-fill'].map((icon, index) => (
                  <a key={index} href="#" className="w-10 h-10 flex items-center justify-center bg-white/10 backdrop-blur-xl hover:bg-red-600 rounded-2xl transition-all duration-300 cursor-pointer hover:scale-110">
                    <i className={icon}></i>
                  </a>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="text-base lg:text-lg font-semibold mb-4 lg:mb-6">Navigacija</h4>
              <ul className="space-y-2 lg:space-y-3">
                {[
                  { name: 'Početna', action: scrollToTop },
                  { name: 'Nekretnine', href: '/nekretnine' },
                  { name: 'Usluge', action: () => scrollToSection('services') },
                  { name: 'Blog', action: () => scrollToSection('blog-tools') },
                  { name: 'Besplatna procjena', href: '/besplatna-procjena-nekretnine-zagreb' },
                  { name: 'Kontakt', action: () => setShowContactModal(true) }
                ].map((link, index) => (
                  <li key={index}>
                    {link.href ? (
                      <a href={link.href} className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm lg:text-base">
                        {link.name}
                      </a>
                    ) : (
                      <button onClick={link.action} className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm lg:text-base">
                        {link.name}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="text-base lg:text-lg font-semibold mb-4 lg:mb-6">Pravni linkovi</h4>
              <ul className="space-y-2 lg:space-y-3">
                {[
                  { name: 'Uvjeti korištenja', href: '/uvjeti-koristenja' },
                  { name: 'Politika privatnosti (GDPR)', href: '/privatnost-gdpr' },
                  { name: 'Politika kolačića', href: '/politika-kolacica' },
                  { name: 'Obrazac za prigovore', href: '/obrazac-za-prigovore' }
                ].map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm lg:text-base">
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-6 lg:pt-8">
            <div className="text-center text-gray-400 text-xs lg:text-sm space-y-1 lg:space-y-2">
              <p className="font-medium">BUY IN ZAGREB d.o.o.</p>
              <p>Kulmerska ulica 23A, 10000 Zagreb, Hrvatska</p>
              <p>OIB: 17682979076</p>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-6 lg:pt-8 mt-6 lg:mt-8 text-center">
            <p className="text-gray-400 text-xs lg:text-sm">© 2024 BuyInZagreb. Sva prava pridržana.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
